import React, { useState } from "react";
import {
  FiArrowRight,
  FiBarChart2,
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiClock,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { subscribeToBlog } from "../Services/BlogService";
import { blogPosts } from "./blogData";

const insights = [
  { value: "Weekly", label: "market and compliance notes" },
  { value: "Actionable", label: "advisory-focused breakdowns" },
  { value: "Practical", label: "guidance for real operating teams" },
];

const resourcePillars = [
  {
    icon: FiBriefcase,
    title: "Business Finance",
    text: "Working capital, cost discipline, funding readiness, and reporting quality.",
  },
  {
    icon: FiBarChart2,
    title: "Tax & Compliance",
    text: "Direct tax, GST hygiene, filing calendars, and review checkpoints.",
  },
  {
    icon: FiBookOpen,
    title: "Professional Insight",
    text: "Executive summaries that translate technical complexity into decisions.",
  },
];

function Blog() {
  const [subscriberEmail, setSubscriberEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const featuredStory = blogPosts[0];
  const latestArticles = blogPosts.slice(1, 4);
  const secondaryArticles = blogPosts.slice(4);

  const handleSubscribe = async () => {
    const normalizedEmail = subscriberEmail.trim();

    if (!normalizedEmail) {
      await Swal.fire({
        icon: "error",
        title: "Email required",
        text: "Please enter your email address.",
      });
      return;
    }

    setSubscribing(true);

    try {
      const response = await subscribeToBlog(normalizedEmail);

      await Swal.fire({
        icon: "success",
        title: "Subscribed",
        text:
          response.data?.message ||
          `Subscribed successfully: ${response.data?.email ?? normalizedEmail}`,
      });

      setSubscriberEmail("");
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Subscription failed",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Unable to subscribe right now.",
      });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-[100%] overflow-hidden bg-body animate-[pageFade_0.6s_ease-out]">
      <div className="px-4 py-6 sm:px-6 lg:py-10">
        <div className="mx-auto max-w-7xl">
          <section className="relative overflow-hidden rounded-[32px] border border-primary-default/10 bg-neutral-white px-6 py-6 shadow-[0_28px_80px_rgba(16,42,107,0.08)] sm:px-8 lg:px-10 lg:py-10">
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-r from-primary-dark via-primary-default to-primary-light opacity-[0.06]" />
            <div className="absolute -left-16 top-12 h-40 w-40 rounded-full bg-accent-gold/20 blur-3xl" />
            <div className="absolute -right-20 bottom-0 h-56 w-56 rounded-full bg-primary-default/10 blur-3xl" />

            <div className="relative grid gap-8 lg:grid-cols-[1.2fr_0.9fr] lg:items-stretch">
              <div className="flex flex-col justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-accent-gold/30 bg-accent-gold/10 md:px-4 px-2 md:py-2 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-primary-default">
                    WealthGrow Insights
                  </div>

                  <h1 className="mt-5 max-w-3xl text-xl font-bold leading-tight text-primary-dark sm:text-2xl lg:text-[2.5rem]">
                    Clarity-driven insights for finance, tax & tech growth
                  </h1>

                  <p className="md:mt-5 mt-3 max-w-2xl md:text-base text-xs md:leading-8 leading-5 text-neutral-textSecondary sm:text-lg">
                    A research-driven editorial and advisory platform covering tax planning, financial strategy, 
                    compliance, and disciplined operations—integrated with technology services to enhance execution 
                    and scalability. Built for founders, finance teams, and professionals, it delivers clear,
                    insight-led guidance that transforms complexity into actionable decisions and measurable outcomes.
                  </p>
                </div>

                <div className="md:mt-8 mt-4 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="grid md:gap-3 gap-2 sm:grid-cols-3 sm:gap-4">
                    {insights.map((item) => (
                      <div
                        key={item.label}
                        className="rounded-2xl border border-neutral-border bg-neutral-light px-4 md:py-3 py-1"
                      >
                        <div className="text-lg font-bold text-primary-dark">
                          {item.value}
                        </div>
                        <div className="text-xs md:leading-5 leading-1 text-neutral-textSecondary">
                          {item.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-default px-6 md:py-3 py-2 
                    text-sm font-semibold text-neutral-white shadow-lg shadow-primary-default/20 transition-all duration-300 
                    hover:-translate-y-0.5 hover:bg-primary-light hover:shadow-[0_30px_70px_rgba(16,42,107,0.3)] hover:text-accent-gold"
                  >
                    Talk to WFS
                    <FiArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <article className="relative overflow-hidden rounded-[28px] border border-primary-default/10 bg-gradient-to-br 
              from-primary-dark via-primary-default to-primary-light p-5 text-white shadow-[0_24px_60px_rgba(16,42,107,0.24)]">
                <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-accent-gold/20 blur-3xl" />
                <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-white/10 blur-3xl" />

                <div className="relative flex h-full flex-col">
                  <div className="md:mb-4 mb-1 flex items-center justify-between md:gap-4 gap-2">
                    <span className="inline-flex rounded-full bg-accent-gold px-3 py-1 text-xs font-bold uppercase tracking-[0.22em] text-primary-dark">
                      {featuredStory.category}
                    </span>
                    <span className="inline-flex items-center gap-2 text-sm text-white/75">
                      <FiClock className="h-4 w-4" />
                      {featuredStory.readTime}
                    </span>
                  </div>

                  <div className="md:mb-4 mb-2 overflow-hidden rounded-[20px] border border-white/10 bg-white/10">
                    <img
                      src={featuredStory.image}
                      alt={featuredStory.title}
                      className="h-[180px] w-full object-cover"
                    />
                  </div>

                  <h2 className="max-w-md md:text-lg text-md font-bold leading-tight">
                    {featuredStory.title}
                  </h2>

                  <p className="mt-2 max-w-lg md:text-2xs text-xs md:leading-7 leading-5 text-gray-400 sm:text-base">
                    {featuredStory.excerpt}
                  </p>

                  <div className="mt-auto flex items-center justify-between gap-4 pt-3">
                    <div>
                      <div className="text-sm font-semibold">
                        {featuredStory.author}
                      </div>
                      <div className="text-xs uppercase tracking-[0.2em] text-white/55">
                        {featuredStory.publishedAt}
                      </div>
                    </div>

                    <Link
                      to={`/blog/${featuredStory.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary-dark transition hover:bg-accent-gold"
                    >
                      Read Insight
                      <FiArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section className="mt-10">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-default">
                  Latest Articles
                </div>
                <h2 className="mt-2 text-2xl font-bold text-primary-dark sm:text-3xl">
                  Curated reading for finance-tech growth
                </h2>
              </div>
              {/* <p className="max-w-xl text-sm leading-7 text-neutral-textSecondary">
                Editorial content structured like a professional knowledge
                center rather than a generic blog feed.
              </p> */}
            </div>

            <div className="grid gap-5 lg:grid-cols-[1.15fr_0.85fr_0.85fr]">
              {latestArticles.map((article, index) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  priority={index === 0}
                />
              ))}
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <SpotlightCard article={featuredStory} />
              <div className="grid gap-5">
                {secondaryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} compact />
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10 grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[30px] border border-primary-default/10 bg-gradient-to-br from-primary-dark via-primary-default 
            to-primary-light p-4 text-white shadow-[0_24px_65px_rgba(16,42,107,0.2)] sm:p-8">
              <div className="inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-accent-gold">
                Newsletter Preview
              </div>
              <h2 className="mt-2 text-md font-bold sm:text-lg">
                Weekly clarity on the numbers that move decisions.
              </h2>
              <p className="mt-2 max-w-xl text-sm md:leading-7 leading-5 text-gray-300 sm:text-base">
                Designed for founders, finance teams, and professionals who
                prefer concise market context, tax awareness, and decision-useful
                commentary.
              </p>

              <div className="mt-4 rounded-[24px] border border-white/10 bg-white/10 p-2 backdrop-blur-sm ">
                <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                  <input
                    type="email"
                    placeholder="Enter your professional email"
                    value={subscriberEmail}
                    onChange={(event) => setSubscriberEmail(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        if (!subscribing) {
                          handleSubscribe();
                        }
                      }
                    }}
                    className="rounded-2xl border border-white/10 bg-white/90 px-4 py-2 text-sm text-primary-dark 
                    outline-none transition focus:border-accent-gold focus:ring-2 focus:ring-accent-gold/30"
                  />
                  <button
                    type="button"
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="rounded-2xl bg-accent-gold px-5 py-2 text-sm font-semibold text-primary-dark transition hover:bg-accent-goldLight"
                  >
                    {subscribing ? "Subscribing..." : "Subscribe"}
                  </button>
                </div>
              </div>
            </div>

            <div className="hidden lg:grid lg:gap-5 lg:grid-cols-1 xl:grid-cols-3">
              {resourcePillars.map(({ icon: Icon, title, text }) => (
                <article
                  key={title}
                  className="rounded-[26px] border border-neutral-border bg-neutral-white p-5 shadow-[0_18px_45px_rgba(16,42,107,0.06)]"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-gold/15 text-primary-default">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-primary-dark">
                    {title}
                  </h3>
                  <p className="mt-3 text-sm md:leading-7 leading-5 text-neutral-textSecondary">
                    {text}
                  </p>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>

      <style>
        {`
          @keyframes pageFade {
            from {
              opacity: 0;
              transform: translateY(16px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
}

function ArticleCard({ article, priority = false, compact = false }) {
  return (
    <article
      className={`group overflow-hidden rounded-[28px] border border-neutral-border bg-neutral-white shadow-[0_18px_45px_rgba(16,42,107,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(16,42,107,0.12)] ${
        compact ? "" : "h-full"
      }`}
    >
      <div
        className={`relative overflow-hidden bg-gradient-to-br ${article.coverTheme} p-5 ${
          article.dark ? "text-white" : "text-primary-dark"
        } ${compact ? "min-h-[240px]" : "min-h-[320px]"}`}
      >
        <div className="absolute -right-6 -top-10 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
        <div className="absolute bottom-0 left-0 h-24 w-24 rounded-full bg-primary-default/10 blur-2xl" />

        <div className="relative flex h-full flex-col">
          <span
            className={`inline-flex w-fit rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] ${
              article.dark
                ? "bg-white/15 text-accent-gold"
                : "bg-primary-default/10 text-primary-default"
            }`}
          >
            {article.category}
          </span>

          <div className={`mt-auto ${priority ? "pt-16" : "pt-10"}`}>
            <div className="overflow-hidden rounded-[22px] border border-white/20 shadow-[0_18px_35px_rgba(16,42,107,0.18)]">
              <img
                src={article.image}
                alt={article.title}
                className={`${compact ? "h-[140px]" : "h-[190px]"} w-full object-cover transition-transform duration-500 group-hover:scale-105`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-neutral-textSecondary">
          <FiClock className="h-3.5 w-3.5" />
          {article.meta}
        </div>

        <h3 className="mt-3 text-xl font-semibold leading-tight text-primary-dark">
          {article.title}
        </h3>
        <p className="mt-3 text-sm md:leading-7 leading-5 text-neutral-textSecondary">
          {article.excerpt}
        </p>

        <Link
          to={`/blog/${article.slug}`}
          className="md:mt-5 mt-2 inline-flex items-center gap-2 text-sm font-semibold text-primary-default transition group-hover:gap-3"
        >
          Read article
          <FiArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

function SpotlightCard({ article }) {
  return (
    <article className="overflow-hidden rounded-[30px] border border-neutral-border bg-neutral-white shadow-[0_18px_45px_rgba(16,42,107,0.06)]">
      <div className="grid gap-0 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="relative min-h-[300px] overflow-hidden bg-[linear-gradient(135deg,_#09173F,_#13357D_60%,_#244C9D)] p-6">
          <img
            src={article.image}
            alt={article.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/45 via-transparent to-transparent" />
        </div>

        <div className="flex flex-col justify-center p-6 sm:p-8">
          <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-default">
            Executive Brief
          </div>
          <h3 className="mt-3 text-2xl font-bold leading-tight text-primary-dark">
            {article.title}
          </h3>
          <p className="mt-4 max-w-2xl text-sm md:leading-7 leading-5 text-neutral-textSecondary sm:text-base">
            {article.excerpt}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-neutral-textSecondary">
            <span className="inline-flex items-center gap-2">
              <FiCalendar className="h-4 w-4 text-primary-default" />
              {article.publishedAt}
            </span>
            <span className="inline-flex items-center gap-2">
              <FiClock className="h-4 w-4 text-primary-default" />
              {article.readTime}
            </span>
          </div>

          <Link
            to={`/blog/${article.slug}`}
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary-default/15 px-5 py-3 text-sm font-semibold text-primary-default transition hover:bg-primary-default hover:text-white"
          >
            Read article
            <FiArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default Blog;
